import { forwardRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { formatISO, getHours, parseISO, setHours } from 'date-fns'

import {
  MIN_DISPLAYED_TIME,
  MIN_DISPLAYED_TIME_PER_HOUR_MODE,
} from '@DS/components/components/datePicker/DatePicker.constants'
import { type Target } from '@DS/components/components/datePicker/DatePicker.types'
import DateSearch, {
  type DateSearchInputProps,
  type DateSearchProps,
} from '@DS/components/navigation/search/dateSearch/DateSearch'
import { useModal, type UseModalStateReturn } from '@Hooks/useModal'

import { type JourneyDatePickerFormData } from './JourneyDatePicker.types'
import JourneyDatePickerWrapper, { type JourneyDatePickerWrapperProps } from './JourneyDatePickerWrapper'

export type JourneyDatePickerProps = Omit<
  JourneyDatePickerWrapperProps,
  'modalProps' | 'onSubmit' | 'isOutwardDateVisible'
> &
  Pick<DateSearchProps, 'size' | 'isElevated'> &
  Pick<DateSearchInputProps, 'autoFocus' | 'isDisabled' | 'isWrapped' | 'onClear'> &
  Required<Pick<JourneyDatePickerWrapperProps, 'initStartDate'>> & {
    isLoading?: boolean
    modalProps?: UseModalStateReturn['modalProps']
    onClick?: VoidFunction
    onDateChange: (outwardDate: Date, inwardDate?: Date) => void
  }

const JourneyDatePicker = forwardRef<HTMLDivElement, JourneyDatePickerProps>(
  (
    {
      autoFocus = false,
      datePickerOverrideTheme,
      initEndDate,
      initStartDate,
      isDisabled = false,
      isInModal = false,
      isLoading = false,
      isWrapped: isSearchCriteriaWrapped = false,
      maxDate,
      modalProps,
      mode = 'SINGLE_START_DATE_WITH_HOURS',
      restrictedDatesList,
      stepHoursMode = 1,
      onClear,
      onClick,
      onDateChange,
      ...rest
    },
    ref,
  ) => {
    const {
      openModal: internalOpenModal,
      closeModal: internalCloseModal,
      modalProps: uncontrolledModalProps,
    } = useModal()

    const hasTimeHidden = mode === 'SINGLE_START_DATE' || mode === 'SINGLE_END_DATE'
    const isInwardDateClearable = (mode === 'ROUND_TRIP' || mode === 'SINGLE_END_DATE') && !initEndDate?.isDateMandatory
    const isInwardFieldVisible =
      mode !== 'SINGLE_START_DATE' && mode !== 'OUTWARD_ONLY' && mode !== 'SINGLE_START_DATE_WITH_HOURS'

    const startDateHours = getHours(initStartDate.date)
    const outwardDate = setHours(
      initStartDate.date,
      Math.max(
        startDateHours - (startDateHours % stepHoursMode),
        stepHoursMode === 1 ? MIN_DISPLAYED_TIME_PER_HOUR_MODE : MIN_DISPLAYED_TIME,
      ),
    )
    const inwardDate = initEndDate?.date

    const [initTarget, setInitTarget] = useState<Target>()

    const JOURNEY_DATE_PICKER_DESCRIBER_ID = 'journeyDatePickerDescriber'

    const handleClick = () => {
      if (!modalProps) {
        internalOpenModal()
      }
      onClick?.()
    }

    const closeModal = () => {
      if (modalProps) {
        modalProps.onClose()
      } else {
        internalCloseModal()
      }
    }

    const handleSumbit = (formData: JourneyDatePickerFormData) => {
      const { startDate, endDate, startDateTime, endDateTime } = formData

      if (hasTimeHidden) {
        onDateChange(parseISO(startDate), endDate ? parseISO(endDate) : undefined)
        closeModal()

        return
      }

      const todayTime = getHours(new Date())
      const oDate = startDateTime
        ? setHours(parseISO(startDate), +startDateTime)
        : setHours(parseISO(startDate), +todayTime)
      const iDate = endDate ? setHours(parseISO(endDate), +endDateTime) : undefined
      onDateChange(oDate, iDate)

      closeModal()
    }

    return (
      <div>
        <span id={JOURNEY_DATE_PICKER_DESCRIBER_ID} hidden>
          <FormattedMessage id="datepicker_openButtonDescriber" />
        </span>
        <DateSearch
          {...rest}
          ref={ref}
          inwardDateInputField={{
            autoFocus: (!isDisabled && mode === 'INWARD' && autoFocus) || undefined,
            isDisabled: mode === 'OUTWARD' || isDisabled,
            isVisible: isInwardFieldVisible,
            buttonLabelId: initEndDate?.buttonLabelId,
            value: inwardDate ? formatISO(inwardDate) : '',
            ...(isInwardDateClearable && {
              onClear: () => {
                onDateChange(outwardDate, undefined)
                onClear?.()
              },
            }),
            onClick: () => {
              setInitTarget('START')
              handleClick()
            },
            'aria-describedby': JOURNEY_DATE_PICKER_DESCRIBER_ID,
            isWrapped: isSearchCriteriaWrapped,
            placeholder: initEndDate?.placeholder,
          }}
          outwardDateInputField={{
            autoFocus: (!isDisabled && mode !== 'INWARD' && autoFocus) || undefined,
            isDisabled: mode === 'INWARD' || isDisabled,
            isVisible: mode !== 'SINGLE_END_DATE',
            buttonLabelId: initStartDate.buttonLabelId,
            value: formatISO(outwardDate),
            onClick: () => {
              setInitTarget('INIT')
              handleClick()
            },
            'aria-describedby': JOURNEY_DATE_PICKER_DESCRIBER_ID,
          }}
          isOutwardOnly={mode === 'OUTWARD_ONLY' || mode === 'SINGLE_START_DATE_WITH_HOURS'}
          hasTimeHidden={hasTimeHidden}
          isCompactedOnMobile={mode !== 'OUTWARD_ONLY' && mode !== 'SINGLE_START_DATE_WITH_HOURS'}
        />
        <JourneyDatePickerWrapper
          datePickerOverrideTheme={datePickerOverrideTheme}
          hasTimeHidden={hasTimeHidden}
          initEndDate={initEndDate}
          initStartDate={{ ...initStartDate, date: outwardDate }}
          initTarget={initTarget}
          isInModal={isInModal}
          isInwardDateVisible={isInwardFieldVisible}
          isLoading={isLoading}
          isOutwardDateVisible={mode !== 'SINGLE_END_DATE'}
          maxDate={maxDate}
          modalProps={modalProps ?? uncontrolledModalProps}
          mode={mode}
          restrictedDatesList={restrictedDatesList}
          stepHoursMode={stepHoursMode}
          onSubmit={handleSumbit}
        />
      </div>
    )
  },
)
export default JourneyDatePicker
